import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import { expendituresService } from '../services/expendituresService';
// P2P v1 — read-only payment status from Xero
import { getPaymentStatus } from '../services/xeroBillsService';
import styles from '../assets/expenseAccount.module.css';

const ExpenseAccount = () => {
  const navigate = useNavigate();
  const location = useLocation();
  
  // State variables
  const [loading, setLoading] = useState(true);
  const [tableLoading, setTableLoading] = useState(false); // Separate loading state for table updates
  const [expenses, setExpenses] = useState([]);
  // Removed - now using tableControls.entriesPerPage and tableControls.search
  const [currentPage, setCurrentPage] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [columnVisibility, setColumnVisibility] = useState({
    property: true,
    category: true,
    type: true,
    po: true,
    invoice: true,
    ticketId: true,
    company: true,
    invoiceDate: true,
    dueDate: true,
    amountDue: true,
    totalPaid: true,
    age: true,
    outstanding: true,
    // P2P v1 — what Xero says about payment. Read-only; nothing here is changed.
    //
    // Off by default, matching Expenditures. Without the Xero Bills permission the API
    // refuses and the column is nothing but dashes; whoever wants it turns it on from Columns.
    xeroPayment: false,
    actions: true
  });
  const [showColumnVisibility, setShowColumnVisibility] = useState(false);

  // Xero payment status for the rows on screen, keyed by expenditure id. Read-only —
  // nothing here writes back to expenditure_items.
  const [xeroPayments, setXeroPayments] = useState({});

  // Sorting states
  const [sortField, setSortField] = useState('');
  const [sortDirection, setSortDirection] = useState('asc');

  // Filter states
  const [filters, setFilters] = useState({
    category_id: '',
    type_id: '',
    bank_account_id: '',
    creditor: '',
    date_from: '',
    date_to: ''
  });

  console.log('xxx',filters)
  
  // Applied filters state - only updated when search is clicked
  const [appliedFilters, setAppliedFilters] = useState({
    category_id: '',
    type_id: '',
    bank_account_id: '',
    creditor: '',
    date_from: '',
    date_to: ''
  });
  
  // State for DataTables controls (matching Expenditures.jsx)
  const [tableControls, setTableControls] = useState({
    entriesPerPage: '30',
    search: ''
  });

  // Applied search state - updated on change (matching Expenditures.jsx)
  const [appliedTableSearch, setAppliedTableSearch] = useState('');

  // Payment summary data
  const [paymentSummary, setPaymentSummary] = useState({
    totalPaid: 0,
    statistics: {
      '0': 0,
      '1_30': 0,
      '31_60': 0,
      '61_90': 0,
      'over_90': 0
    },
    totalOutstanding: 0,
    totalAmountDueToday: 0
  });

  // Filter options - will be loaded from API
  const [filterOptions, setFilterOptions] = useState({
    categories: [{ id: '', name: 'All' }],
    types: [{ id: '', name: 'All' }],
    bankAccounts: [{ id: '', name: 'All' }]
  });

  // Track if component has mounted to avoid double loading
  const [hasMounted, setHasMounted] = useState(false);
  const isInitialLoadRef = useRef(true);

  // Load dropdown data on component mount
  useEffect(() => {
    loadDropdownData();
  }, []);

  // Cleanup debounce timer on unmount
  useEffect(() => {
    return () => {
      if (searchDebounceTimer.current) {
        clearTimeout(searchDebounceTimer.current);
      }
    };
  }, []);

  // Reset to first page when search changes (matching Expenditures.jsx)
  // Removed - this was causing re-renders. Page reset is handled in handleTableControlChange

  // Update total pages when total records change
  useEffect(() => {
    const entriesPerPage = parseInt(tableControls.entriesPerPage, 10) || 30;
    const totalPages = Math.ceil(totalRecords / entriesPerPage);
    setTotalPages(totalPages);
    
    if (currentPage > totalPages && totalPages > 0) {
      setCurrentPage(1);
    }
  }, [totalRecords, tableControls.entriesPerPage, currentPage]);

  // Load dropdown filter options from API
  const loadDropdownData = async () => {
    try {
      console.log("[EXPENSE_ACCOUNT] Loading dropdown filter data...");
      const token = localStorage.getItem('token');
      const API_BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:3001";
      
      const response = await fetch(`${API_BASE_URL}/api/finance/expense-account/form-data`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': token ? `Bearer ${token}` : '',
        }
      });
      
      if (!response.ok) {
        throw new Error(`API response not ok: ${response.status} ${response.statusText}`);
      }
      
      const data = await response.json();
      console.log("[EXPENSE_ACCOUNT] Form data response:", data);
      
      if (data.success && data.data) {
        // Format data for dropdowns: Add "All" option and format as { id, name }
        const categories = [{ id: '', name: 'All' }, ...(data.data.categories || []).map(cat => ({ id: String(cat.id), name: cat.name }))];
        const types = [{ id: '', name: 'All' }, ...(data.data.types || []).map(type => ({ id: String(type.id), name: type.name }))];
        const bankAccounts = [{ id: '', name: 'All' }, ...(data.data.bankAccounts || []).map(acc => ({ id: String(acc.id), name: acc.name }))];
        
        setFilterOptions({
          categories,
          types,
          bankAccounts
        });
        
        console.log("[EXPENSE_ACCOUNT] Dropdown data loaded successfully:", {
          categories: categories.length,
          types: types.length,
          bankAccounts: bankAccounts.length
        });
      } else {
        console.warn("[EXPENSE_ACCOUNT] Form data response missing success or data");
      }
    } catch (error) {
      console.error("[EXPENSE_ACCOUNT] Error loading dropdown data:", error);
      // Keep default "All" option on error
    }
  };

  const loadExpenses = useCallback(async (overrideFilters = null, overrideSearch = null, overridePage = null, isInitialLoad = false, overrideSortField = null, overrideSortDirection = null) => {
    // Use tableLoading for subsequent loads to avoid full page re-render
    if (isInitialLoad) {
      setLoading(true);
    } else {
      setTableLoading(true);
    }
    try {
      console.log("[EXPENSE_ACCOUNT] Calling real API...");
      const token = localStorage.getItem('token');
      console.log("[EXPENSE_ACCOUNT] Token found:", !!token);

      if (!token) {
        console.warn("[EXPENSE_ACCOUNT] No authentication token found. User may need to log in.");
        toast.error("No authentication token found. Please log in.");
        setExpenses([]);
        setTotalRecords(0);
        setPaymentSummary({
          totalPaid: 0,
          statistics: { '0': 0, '1_30': 0, '31_60': 0, '61_90': 0, 'over_90': 0 },
          totalOutstanding: 0,
          totalAmountDueToday: 0
        });
        return;
      }

      // Use override values if provided, otherwise use state
      const filtersToUse = overrideFilters !== null ? overrideFilters : appliedFilters;
      const searchToUse = overrideSearch !== null ? overrideSearch : appliedTableSearch;
      const pageToUse = overridePage !== null ? overridePage : currentPage;
      const entriesPerPage = parseInt(tableControls.entriesPerPage, 10) || 30;
      const sortFieldToUse = overrideSortField !== null ? overrideSortField : sortField;
      const sortDirectionToUse = overrideSortDirection !== null ? overrideSortDirection : sortDirection;

      console.log("[EXPENSE_ACCOUNT] Using filters:", filtersToUse);
      console.log("[EXPENSE_ACCOUNT] Using search:", searchToUse);
      console.log("[EXPENSE_ACCOUNT] Using page:", pageToUse);
      console.log("[EXPENSE_ACCOUNT] Using sort:", sortFieldToUse, sortDirectionToUse);

      const API_BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:3001";
      const response = await fetch(`${API_BASE_URL}/api/finance/expense-account`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': token ? `Bearer ${token}` : '',
        },
        body: JSON.stringify({
          ...filtersToUse,
          search: { value: searchToUse },
          start: (pageToUse - 1) * entriesPerPage,
          length: entriesPerPage,
          draw: 1,
          // Server-side sorting parameters
          sortField: sortFieldToUse,
          sortDirection: sortDirectionToUse
        })
      });
      
      if (!response.ok) {
        throw new Error(`API response not ok: ${response.status} ${response.statusText}`);
      }
      
      const data = await response.json();
      console.log("[EXPENSE_ACCOUNT] API response:", data);
      
      if (data.success) {
        setExpenses(data.data || []);
        // Use recordsFiltered for pagination (filtered count) or recordsTotal (total count)
        setTotalRecords(data.recordsFiltered || data.recordsTotal || 0);
        
        // Use payment summary from backend (calculated from ALL expenses, matching Laravel)
        if (data.paymentSummary) {
          // Backend provides payment summary calculated from ALL expenses
          setPaymentSummary({
            totalPaid: data.paymentSummary.totalPaid || 0,
            statistics: {
              '0': data.paymentSummary.statistics?.['0'] || 0,
              '1_30': data.paymentSummary.statistics?.['1_30'] || 0,
              '31_60': data.paymentSummary.statistics?.['31_60'] || 0,
              '61_90': data.paymentSummary.statistics?.['61_90'] || 0,
              'over_90': data.paymentSummary.statistics?.over_90 || 0,
            },
            totalOutstanding: data.paymentSummary.totalOutstanding || 0,
            totalAmountDueToday: data.paymentSummary.totalAmountDueToday || 0
          });
          console.log("[EXPENSE_ACCOUNT] Payment summary loaded from backend:", data.paymentSummary);
        } else {
          // Fallback: Empty payment summary if backend doesn't provide it
          console.warn("[EXPENSE_ACCOUNT] No payment summary in API response, using empty summary");
          setPaymentSummary({
            totalPaid: 0,
            statistics: { '0': 0, '1_30': 0, '31_60': 0, '61_90': 0, 'over_90': 0 },
            totalOutstanding: 0,
            totalAmountDueToday: 0
          });
        }
        
        console.log("[EXPENSE_ACCOUNT] Successfully loaded from API");
      } else {
        throw new Error(data.message || "API returned unsuccessful response");
      }
    } catch (apiError) {
      console.error("[EXPENSE_ACCOUNT] API failed:", apiError.message);
      toast.error(`Failed to load expenses: ${apiError.message}`);
      setExpenses([]);
      setTotalRecords(0);
      setPaymentSummary({
        totalPaid: 0,
        statistics: { '0': 0, '1_30': 0, '31_60': 0, '61_90': 0, 'over_90': 0 },
        totalOutstanding: 0,
        totalAmountDueToday: 0
      });
    } finally {
      setLoading(false);
      setTableLoading(false);
    }
  }, [appliedFilters, appliedTableSearch, currentPage, tableControls.entriesPerPage, sortField, sortDirection]);

  // Load data when page or entries per page changes
  // Use separate state for entriesPerPage to prevent unnecessary re-renders
  const entriesPerPage = parseInt(tableControls.entriesPerPage, 10) || 30;
  useEffect(() => {
    // Pass isInitialLoad=true only on first mount
    loadExpenses(null, null, null, isInitialLoadRef.current);
    isInitialLoadRef.current = false;
    setHasMounted(true);
  }, [currentPage, entriesPerPage, loadExpenses]);

  // Reload data when navigating back to this page (e.g., after creating/editing)
  useEffect(() => {
    // Only reload if component has already mounted (to avoid double loading on initial mount)
    if (hasMounted && (location.pathname === '/finance/expense-account' || location.pathname === '/finances/expense-account')) {
      console.log("[EXPENSE_ACCOUNT] Reloading data after navigation");
      loadExpenses();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, hasMounted]);

  // Calculate age helper function - moved before first usage in sorting
  const calculateAge = (dueDate) => {
    if (!dueDate) return 0;

    let due;

    // Handle DD/MM/YYYY format (from backend)
    if (typeof dueDate === 'string' && dueDate.includes('/')) {
      const parts = dueDate.split('/');
      if (parts.length === 3) {
        const day = parseInt(parts[0], 10);
        const month = parseInt(parts[1], 10);
        const year = parseInt(parts[2], 10);

        if (!isNaN(day) && !isNaN(month) && !isNaN(year)) {
          due = new Date(year, month - 1, day);
        } else {
          return 0;
        }
      } else {
        due = new Date(dueDate);
      }
    } else {
      due = new Date(dueDate);
    }

    if (isNaN(due.getTime())) return 0;

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    due.setHours(0, 0, 0, 0);

    return due < today ? Math.floor((today - due) / (1000 * 60 * 60 * 24)) : 0;
  };

  const getFilteredExpenses = () => {
    let filtered = expenses;

    // Apply search filter (now using tableControls.search directly for instant filtering)
    if (tableControls.search) {
      const searchLower = tableControls.search.toLowerCase();
      const searchValue = tableControls.search.trim();
      // Check if search is numeric (for amount searches)
      const isNumeric = !isNaN(parseFloat(searchValue)) && isFinite(searchValue);
      
      filtered = filtered.filter(expense => {
        // Text field searches
        const textMatch = 
          expense.propertyName?.toLowerCase().includes(searchLower) ||
          expense.categoryName?.toLowerCase().includes(searchLower) ||
          expense.typeName?.toLowerCase().includes(searchLower) ||
          expense.creditor?.toLowerCase().includes(searchLower) ||
          expense.invoice_number?.toLowerCase().includes(searchLower) ||
          expense.purchase_order_number?.toLowerCase().includes(searchLower);
        
        // Amount field searches (if numeric or always check as string)
        const amountMatch = 
          String(expense.amount || '').includes(searchValue) ||
          String(expense.amount_paid || '').includes(searchValue) ||
          String(expense.amount_outstanding || '').includes(searchValue) ||
          String(expense.amountDue || '').includes(searchValue) ||
          String(expense.totalPaid || '').includes(searchValue) ||
          String(expense.outstanding || '').includes(searchValue);
        
        return textMatch || amountMatch;
      });
    }

    // Apply filters
    if (filters.category_id && filters.category_id !== 'All') {
      filtered = filtered.filter(expense => expense.categoryName === filters.category_id);
    }
    if (filters.type_id && filters.type_id !== 'All') {
      filtered = filtered.filter(expense => expense.typeName === filters.type_id);
    }
    if (filters.bank_account_id && filters.bank_account_id !== 'All') {
      filtered = filtered.filter(expense => expense.bankAccountName === filters.bank_account_id);
    }
    if (filters.creditor) {
      filtered = filtered.filter(expense =>
        expense.creditor?.toLowerCase().includes(filters.creditor.toLowerCase())
      );
    }
    if (filters.date_from) {
      filtered = filtered.filter(expense => expense.date >= filters.date_from);
    }
    if (filters.date_to) {
      filtered = filtered.filter(expense => expense.date <= filters.date_to);
    }

    // Apply sorting
    if (sortField) {
      filtered.sort((a, b) => {
        let aValue, bValue;

        switch (sortField) {
          case 'property':
            aValue = a.propertyName || '';
            bValue = b.propertyName || '';
            break;
          case 'category':
            aValue = a.categoryName || '';
            bValue = b.categoryName || '';
            break;
          case 'type':
            aValue = a.typeName || '';
            bValue = b.typeName || '';
            break;
          case 'po':
            aValue = a.purchase_order_number || '';
            bValue = b.purchase_order_number || '';
            break;
          case 'invoice':
            aValue = a.invoice_number || '';
            bValue = b.invoice_number || '';
            break;
          case 'ticketId':
            aValue = a.ticket_id || 0;
            bValue = b.ticket_id || 0;
            break;
          case 'company':
            aValue = a.creditor || '';
            bValue = b.creditor || '';
            break;
          case 'invoiceDate':
            aValue = new Date(a.date || '1970-01-01');
            bValue = new Date(b.date || '1970-01-01');
            break;
          case 'dueDate':
            // Handle DD/MM/YYYY format
            const parseDate = (dateStr) => {
              if (!dateStr) return new Date('1970-01-01');
              if (typeof dateStr === 'string' && dateStr.includes('/')) {
                const parts = dateStr.split('/');
                if (parts.length === 3) {
                  const day = parseInt(parts[0], 10);
                  const month = parseInt(parts[1], 10);
                  const year = parseInt(parts[2], 10);
                  if (!isNaN(day) && !isNaN(month) && !isNaN(year)) {
                    return new Date(year, month - 1, day);
                  }
                }
              }
              return new Date(dateStr || '1970-01-01');
            };
            aValue = parseDate(a.due_date);
            bValue = parseDate(b.due_date);
            break;
          case 'amountDue':
            aValue = Number(a.amount || 0);
            bValue = Number(b.amount || 0);
            break;
          case 'totalPaid':
            aValue = Number(a.amount_paid || 0);
            bValue = Number(b.amount_paid || 0);
            break;
          case 'age':
            aValue = calculateAge(a.due_date);
            bValue = calculateAge(b.due_date);
            break;
          case 'outstanding':
            aValue = Number(a.amount || 0) - Number(a.amount_paid || 0);
            bValue = Number(b.amount || 0) - Number(b.amount_paid || 0);
            break;
          default:
            return 0;
        }

        // Handle null/undefined values
        if (!aValue && !bValue) return 0;
        if (!aValue) return 1;
        if (!bValue) return -1;

        // Compare values
        if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
        if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
        return 0;
      });
    }

    return filtered;
  };

  // Backend handles pagination, filtering, AND sorting across all pages
  // No client-side sorting needed - data comes pre-sorted from the server
  const paginatedExpenses = useMemo(() => {
    return expenses;
  }, [expenses]);

  // Fetch Xero payment status for the rows on screen. One call per page rather than per row.
  // Declared here — above the `if (loading)` return further down — because a hook placed
  // after an early return is skipped on the renders that take it, which breaks the rules of
  // hooks. Failures are silent: this is supplementary and must never stop the list rendering.
  const visibleExpenseIds = useMemo(
    () => (paginatedExpenses || []).map((e) => e.id).filter(Boolean),
    [paginatedExpenses]
  );

  useEffect(() => {
    if (!columnVisibility.xeroPayment || !visibleExpenseIds.length) {
      setXeroPayments({});
      return;
    }

    let cancelled = false;
    getPaymentStatus(visibleExpenseIds)
      .then((res) => {
        if (!cancelled && res?.success) setXeroPayments(res.data || {});
      })
      .catch(() => {
        /* supplementary only — leave the column blank */
      });

    return () => {
      cancelled = true;
    };
  }, [visibleExpenseIds, columnVisibility.xeroPayment]);

  // Handlers
  const handleFilterChange = (field, value) => {
    // Ensure value is always a string, not an object
    const stringValue = value === null || value === undefined 
      ? '' 
      : (typeof value === 'object' ? '' : String(value));
    setFilters(prev => ({ ...prev, [field]: stringValue }));
    console.log(`[EXPENSE_ACCOUNT] Filter changed: ${field} =`, stringValue, '(type:', typeof stringValue, ')');
  };

  // Ref to store debounce timer for API calls
  const searchDebounceTimer = useRef(null);
  const searchInputRef = useRef(null);
  // Ref to track if search input should maintain focus (persists across rerenders)
  const shouldMaintainFocusRef = useRef(false);
  // Ref to store cursor position
  const cursorPositionRef = useRef(null);

  // Restore focus after every render if input should maintain focus
  useEffect(() => {
    if (shouldMaintainFocusRef.current && searchInputRef.current) {
      // Use setTimeout to ensure this runs after React's render cycle
      const timeoutId = setTimeout(() => {
        if (searchInputRef.current && shouldMaintainFocusRef.current) {
          searchInputRef.current.focus();
          // Restore cursor position if we have it stored
          if (cursorPositionRef.current !== null) {
            searchInputRef.current.setSelectionRange(
              cursorPositionRef.current,
              cursorPositionRef.current
            );
          } else {
            // Otherwise, set cursor to end
            const length = searchInputRef.current.value.length;
            searchInputRef.current.setSelectionRange(length, length);
          }
        }
      }, 0);
      
      return () => clearTimeout(timeoutId);
    }
  }); // Run after every render

  // Handle table control changes (matching Expenditures.jsx exactly)
  const handleTableControlChange = (field, value) => {
    setTableControls(prev => ({ ...prev, [field]: value }));
    
    // If it's a search field, debounce the API call (server-side filtering needs debouncing)
    if (field === 'search') {
      // Clear existing debounce timer
      if (searchDebounceTimer.current) {
        clearTimeout(searchDebounceTimer.current);
      }
      
      // Debounce API call (server-side filtering needs debouncing)
      const searchValue = value ? String(value).trim() : "";
      searchDebounceTimer.current = setTimeout(() => {
        // Only update appliedTableSearch when debounce completes - prevents re-renders during typing
        setAppliedTableSearch(searchValue);
        setCurrentPage(1);
        loadExpenses(appliedFilters, searchValue, 1);
      }, 300);
    }
    
    // If it's entriesPerPage, trigger API call
    if (field === 'entriesPerPage') {
      setCurrentPage(1);
      loadExpenses();
    }
  };

  // Handle search input focus
  const handleSearchFocus = () => {
    shouldMaintainFocusRef.current = true;
    if (searchInputRef.current) {
      cursorPositionRef.current = searchInputRef.current.selectionStart;
    }
  };

  // Handle search input blur
  const handleSearchBlur = () => {
    // Only stop maintaining focus if user explicitly clicked away
    // Don't stop if it's just a rerender
    setTimeout(() => {
      if (searchInputRef.current !== document.activeElement) {
        shouldMaintainFocusRef.current = false;
        cursorPositionRef.current = null;
      }
    }, 0);
  };

  // Handle search input change and track cursor position
  const handleSearchInputChange = (e) => {
    const value = e.target.value;
    // Store cursor position before state update
    cursorPositionRef.current = e.target.selectionStart;
    // Ensure we maintain focus
    shouldMaintainFocusRef.current = true;
    handleTableControlChange('search', value);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    // Apply filters and search when search button is clicked (matching Expenditures.jsx)
    // Normalize filter values: empty string, null, undefined, or "All" means no filter
    const normalizeFilter = (value) => {
      if (!value || value === "" || value === "All" || value === null || value === undefined) {
        return "";
      }
      return String(value).trim();
    };
    
    const newFilters = {
      category_id: normalizeFilter(filters.category_id),
      type_id: normalizeFilter(filters.type_id),
      bank_account_id: normalizeFilter(filters.bank_account_id),
      creditor: filters.creditor ? String(filters.creditor).trim() : "",
      date_from: filters.date_from ? String(filters.date_from).trim() : "",
      date_to: filters.date_to ? String(filters.date_to).trim() : ""
    };
    const newSearch = tableControls.search ? String(tableControls.search).trim() : "";
    
    console.log("[EXPENSE_ACCOUNT] Search submitted with filters:", newFilters);
    console.log("[EXPENSE_ACCOUNT] Search submitted with search term:", newSearch);
    console.log("[EXPENSE_ACCOUNT] Original filter values:", filters);
    
    // Update state
    setAppliedFilters(newFilters);
    setAppliedTableSearch(newSearch);
    setCurrentPage(1); // Reset to first page when searching
    
    // Call loadExpenses with new values directly to avoid state timing issues
    // Pass page 1 directly since we're resetting to first page
    loadExpenses(newFilters, newSearch, 1);
  };

  const handleClearFilters = () => {
    const clearedFilters = {
      category_id: '',
      type_id: '',
      bank_account_id: '',
      creditor: '',
      date_from: '',
      date_to: ''
    };
    // Update state (matching Expenditures.jsx pattern)
    setFilters(clearedFilters);
    setTableControls(prev => ({ ...prev, search: '' }));
    setAppliedFilters(clearedFilters);
    setAppliedTableSearch('');
    setCurrentPage(1); // Reset to first page when clearing filters
    
    // Use setTimeout to ensure state updates before loading expenses (matching Expenditures.jsx)
    setTimeout(() => {
      loadExpenses(clearedFilters, '', 1);
    }, 0);
  };

  const goToPage = (page) => {
    setCurrentPage(page);
  };

  const goToPreviousPage = () => {
    setCurrentPage(prev => Math.max(prev - 1, 1));
  };

  const goToNextPage = () => {
    setCurrentPage(prev => Math.min(prev + 1, totalPages));
  };

  const getPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 7; // Show max 7 page buttons
    
    if (totalPages <= maxVisiblePages) {
      // Show all pages if total is less than max
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Show first page
      pages.push(1);
      
      // Calculate start and end of visible page range
      let startPage = Math.max(2, currentPage - 1);
      let endPage = Math.min(totalPages - 1, currentPage + 1);
      
      // Adjust range if we're near the start
      if (currentPage <= 3) {
        startPage = 2;
        endPage = Math.min(5, totalPages - 1);
      }
      
      // Adjust range if we're near the end
      if (currentPage >= totalPages - 2) {
        startPage = Math.max(2, totalPages - 4);
        endPage = totalPages - 1;
      }
      
      // Add ellipsis before range if needed
      if (startPage > 2) {
        pages.push('ellipsis-start');
      }
      
      // Add pages in range
      for (let i = startPage; i <= endPage; i++) {
        pages.push(i);
      }
      
      // Add ellipsis after range if needed
      if (endPage < totalPages - 1) {
        pages.push('ellipsis-end');
      }
      
      // Show last page
      pages.push(totalPages);
    }
    
    return pages;
  };

  const handleColumnVisibility = () => {
    setShowColumnVisibility(!showColumnVisibility);
  };

  const toggleColumnVisibility = (columnKey) => {
    setColumnVisibility(prev => ({
      ...prev,
      [columnKey]: !prev[columnKey]
    }));
  };

  const handleExportCSV = async () => {
    try {
      const filtered = getFilteredExpenses();
      
      // Create CSV content with all visible columns
      const headers = [];
      const dataRows = [];
      
      // Add headers based on column visibility
      if (columnVisibility.property) headers.push('Property');
      if (columnVisibility.category) headers.push('Category');
      if (columnVisibility.type) headers.push('Type');
      if (columnVisibility.po) headers.push('PO Number');
      if (columnVisibility.invoice) headers.push('Invoice Number');
      if (columnVisibility.ticketId) headers.push('Ticket ID');
      if (columnVisibility.company) headers.push('Company');
      if (columnVisibility.invoiceDate) headers.push('Invoice Date');
      if (columnVisibility.dueDate) headers.push('Due Date');
      if (columnVisibility.amountDue) headers.push('Amount Due');
      if (columnVisibility.totalPaid) headers.push('Total Paid');
      if (columnVisibility.age) headers.push('Age');
      if (columnVisibility.outstanding) headers.push('Outstanding');
      
      // Add data rows
      filtered.forEach(expense => {
        const row = [];
        if (columnVisibility.property) row.push(expense.propertyName);
        if (columnVisibility.category) row.push(expense.categoryName);
        if (columnVisibility.type) row.push(expense.typeName);
        if (columnVisibility.po) row.push(expense.purchase_order_number);
        if (columnVisibility.invoice) row.push(expense.invoice_number);
        if (columnVisibility.ticketId) row.push(expense.ticket_id || '');
        if (columnVisibility.company) row.push(expense.creditor);
        if (columnVisibility.invoiceDate) row.push(formatDate(expense.date));
        if (columnVisibility.dueDate) row.push(formatDate(expense.due_date));
        if (columnVisibility.amountDue) row.push(formatCurrency(expense.amount));
        if (columnVisibility.totalPaid) row.push(formatCurrency(expense.amount_paid));
        if (columnVisibility.age) row.push(calculateAge(expense.due_date));
        if (columnVisibility.outstanding) row.push(formatCurrency(expense.amount_outstanding || (expense.amount - expense.amount_paid)));
        dataRows.push(row);
      });

      const csvContent = [
        headers.join(','),
        ...dataRows.map(row => row.map(field => `"${field}"`).join(','))
      ].join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `expense-account-${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      toast.success('CSV exported successfully!');
    } catch (error) {
      console.error('Export CSV error:', error);
      toast.error('Failed to export CSV');
    }
  };

  const handleExportPDF = async () => {
    try {
      const filtered = getFilteredExpenses();
      
      // Create PDF content using jsPDF
      const { jsPDF } = await import('jspdf');
      const doc = new jsPDF();
      
      // Add title
      doc.setFontSize(16);
      doc.text('Expense Account Report', 20, 20);
      doc.setFontSize(10);
      doc.text(`Generated on: ${new Date().toLocaleDateString('en-GB')}`, 20, 30);
      doc.text(`Total Records: ${filtered.length}`, 20, 40);
      
      // Add payment summary
      doc.setFontSize(12);
      doc.text('Payment Summary:', 20, 55);
      doc.setFontSize(10);
      doc.text(`Total Paid: ${formatCurrency(paymentSummary.totalPaid)}`, 20, 65);
      doc.text(`Total Outstanding: ${formatCurrency(paymentSummary.totalOutstanding)}`, 20, 75);
      doc.text(`Due Today: ${formatCurrency(paymentSummary.totalAmountDueToday)}`, 20, 85);
      
      // Add table headers
      const headers = [];
      if (columnVisibility.dueDate) headers.push('Due Date');
      if (columnVisibility.amountDue) headers.push('Amount Due');
      if (columnVisibility.totalPaid) headers.push('Total Paid');
      if (columnVisibility.age) headers.push('Age');
      if (columnVisibility.outstanding) headers.push('Outstanding');
      if (columnVisibility.property) headers.push('Property');
      if (columnVisibility.company) headers.push('Company');
      
      let yPosition = 110;
      const startX = 20;
      const colWidth = 25;
      
      // Draw headers
      headers.forEach((header, index) => {
        doc.text(header, startX + (index * colWidth), yPosition);
      });
      
      yPosition += 10;
      
      // Draw data rows
      filtered.forEach((expense, index) => {
        if (yPosition > 280) {
          doc.addPage();
          yPosition = 20;
        }
        
        const rowData = [];
        if (columnVisibility.dueDate) rowData.push(formatDate(expense.due_date));
        if (columnVisibility.amountDue) rowData.push(formatCurrency(expense.amount));
        if (columnVisibility.totalPaid) rowData.push(formatCurrency(expense.amount_paid));
        if (columnVisibility.age) rowData.push(calculateAge(expense.due_date).toString());
        if (columnVisibility.outstanding) rowData.push(formatCurrency(expense.amount_outstanding || (expense.amount - expense.amount_paid)));
        if (columnVisibility.property) rowData.push(expense.propertyName.substring(0, 15));
        if (columnVisibility.company) rowData.push(expense.creditor.substring(0, 15));
        
        rowData.forEach((cell, cellIndex) => {
          doc.text(cell.substring(0, 20), startX + (cellIndex * colWidth), yPosition);
        });
        
        yPosition += 7;
      });
      
      // Save PDF
      doc.save(`expense-account-${new Date().toISOString().split('T')[0]}.pdf`);
      
      toast.success('PDF exported successfully!');
    } catch (error) {
      console.error('Export PDF error:', error);
      toast.error('Failed to export PDF');
    }
  };

  const handlePrintTable = () => {
    const printWindow = window.open('', '_blank');
    const filtered = getFilteredExpenses();
    
    const printHTML = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Expense Account - Print</title>
        <style>
          body { font-family: Arial, sans-serif; margin: 20px; }
          h1 { color: #333; text-align: center; margin-bottom: 30px; }
          table { width: 100%; border-collapse: collapse; margin-bottom: 20px; }
          th, td { border: 1px solid #ddd; padding: 8px; text-align: left; font-size: 12px; }
          th { background-color: #f8f9fa; font-weight: bold; }
          .header { margin-bottom: 20px; }
          .date { text-align: right; color: #666; font-size: 12px; }
          @media print {
            body { margin: 0; }
            .no-print { display: none; }
          }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>Expense Account Report</h1>
          <div class="date">Generated on: ${new Date().toLocaleDateString('en-GB')}</div>
        </div>
        <table>
          <thead>
            <tr>
              <th>Property</th>
              <th>Category</th>
              <th>Type</th>
              <th>PO</th>
              <th>Invoice</th>
              <th>Ticket ID</th>
              <th>Company</th>
              <th>Invoice Date</th>
              <th>Due Date</th>
              <th>Amount Due</th>
              <th>Total Paid</th>
              <th>Age</th>
              <th>Outstanding</th>
            </tr>
          </thead>
          <tbody>
            ${filtered.map(expense => `
              <tr>
                <td>${expense.propertyName}</td>
                <td>${expense.categoryName}</td>
                <td>${expense.typeName}</td>
                <td>${expense.purchase_order_number}</td>
                <td>${expense.invoice_number}</td>
                <td>${expense.ticket_id || ''}</td>
                <td>${expense.creditor}</td>
                <td>${formatDate(expense.date)}</td>
                <td>${formatDate(expense.due_date)}</td>
                <td>${formatCurrency(expense.amount)}</td>
                <td>${formatCurrency(expense.amount_paid)}</td>
                <td>${calculateAge(expense.due_date)}</td>
                <td>${formatCurrency(expense.amount_outstanding || (expense.amount - expense.amount_paid))}</td>
              </tr>
            `).join('')}
          </tbody>
        </table>
        <div class="no-print">
          <button onclick="window.print()">Print</button>
          <button onclick="window.close()">Close</button>
        </div>
      </body>
      </html>
    `;

    printWindow.document.write(printHTML);
    printWindow.document.close();
    printWindow.onload = () => {
      printWindow.print();
    };
    
    toast.success('Print window opened');
  };

  const handleExpenseAction = async (action, expenseId) => {
    switch (action) {
      case 'edit':
        navigate(`/finance/expense-account/${expenseId}/edit`);
        break;
      case 'pay':
        navigate(`/finance/expense-account/${expenseId}/pay`);
        break;
      case 'delete':
        if (window.confirm('Are you sure you want to remove this expense and all related records? This action cannot be undone.')) {
          try {
            // Find the expense to get its details for confirmation
            const expense = expenses.find(exp => exp.id === expenseId);
            const expenseName = expense ? `${expense.propertyName} - ${expense.invoice_number}` : `Expense ID ${expenseId}`;
            
            // Call backend API to delete expense and all related records
            const response = await fetch(`${process.env.REACT_APP_API_URL}/api/finance/expense-account/${expenseId}`, {
              method: 'DELETE',
              headers: {
                'Authorization': `Bearer ${localStorage.getItem('token')}`,
                'Content-Type': 'application/json'
              }
            });

            if (response.ok) {
              await response.json();
              toast.success(`Expense "${expenseName}" and all related records deleted successfully!`);

              // Remove from local state
              setExpenses(prevExpenses => prevExpenses.filter(exp => exp.id !== expenseId));

              // Reload data to update totals
              loadExpenses();
            } else {
              let message = 'Failed to delete expense';
              try {
                const errorData = await response.json();
                message = errorData.message || message;
              } catch (_) {
                if (response.status === 404) message = 'Expense not found.';
                else if (response.status >= 500) message = 'Server error. Please try again.';
              }
              toast.error(message);
            }
          } catch (error) {
            console.error('Error deleting expense:', error);
            toast.error('Failed to delete expense. Please try again.');
          }
        }
        break;
      case 'download':
        try {
          // Find the specific expense
          const expense = expenses.find(exp => exp.id === expenseId);
          if (!expense || !expense.file) {
            toast.error('No file available for download');
            return;
          }

          // Use service method which fetches presigned URL and triggers direct download
          await expendituresService.downloadFile(expenseId, 'file');
          
          toast.success('File download started!');
        } catch (error) {
          console.error('Error downloading file:', error);
          toast.error('Failed to download file');
        }
        break;
      default:
        break;
    }
  };

  const formatCurrency = (amount) => {
    return `£${Number(amount || 0).toLocaleString('en-GB', { minimumFractionDigits: 2 })}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    
    // Handle DD/MM/YYYY format (from backend)
    if (typeof dateString === 'string' && dateString.includes('/')) {
      const parts = dateString.split('/');
      if (parts.length === 3) {
        const day = parseInt(parts[0], 10);
        const month = parseInt(parts[1], 10);
        const year = parseInt(parts[2], 10);
        
        // Validate date parts
        if (!isNaN(day) && !isNaN(month) && !isNaN(year) && day > 0 && day <= 31 && month > 0 && month <= 12) {
          const date = new Date(year, month - 1, day);
          // Verify the date is valid (handles invalid dates like 31/02/2025)
          if (date.getDate() === day && date.getMonth() === month - 1 && date.getFullYear() === year) {
            return date.toLocaleDateString('en-GB');
          }
        }
      }
    }
    
    // Try standard Date parsing (for ISO format or other formats)
    const date = new Date(dateString);
    if (!isNaN(date.getTime())) {
      return date.toLocaleDateString('en-GB');
    }
    
    // If all parsing fails, return the original string or '-'
    return '-';
  };

  const formatDateForInput = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toISOString().split('T')[0];
  };

  // Sorting functions - triggers server-side sorting across ALL pages
  const handleSort = (field) => {
    let newSortField = field;
    let newSortDirection = 'asc';

    if (sortField === field) {
      // Toggle direction if clicking same field
      newSortDirection = sortDirection === 'asc' ? 'desc' : 'asc';
    }

    // Update state
    setSortField(newSortField);
    setSortDirection(newSortDirection);

    // Trigger API reload with new sort parameters (server-side sorting)
    // Reset to page 1 when sort changes to show results from the beginning
    setCurrentPage(1);
    loadExpenses(appliedFilters, appliedTableSearch, 1, false, newSortField, newSortDirection);
  };

  const getSortIcon = (field) => {
    if (sortField !== field) {
      return <span style={{ marginLeft: '5px', opacity: 0.4, fontSize: '12px' }}>⇅</span>;
    }
    return (
      <span style={{ marginLeft: '5px', fontSize: '12px' }}>
        {sortDirection === 'asc' ? '▲' : '▼'}
      </span>
    );
  };

  if (loading) {
    return (
      <div className={styles.expenseAccountContainer}>
        <div className={styles.loading}>Loading expense account data...</div>
      </div>
    );
  }

  return (
    <div className={styles.expenseAccountContainer}>
      {/* Breadcrumb */}
      <div className={styles.breadcrumb}>
        <span onClick={() => navigate('/dashboard')}>Dashboard</span> / 
        {/* <span onClick={() => navigate('/finances')}>Finances</span> /  */}
        Expense Account
      </div>

      {/* Payment Summary */}
      <div className={styles.paymentSummarySection}>
        <div className={styles.paymentSummaryTable}>
          <table>
            <tbody>
              <tr className={styles.infoRow}>
                <td className={styles.summaryLabel}><strong>Payment Summary</strong></td>
                <td>{new Date().toLocaleDateString('en-GB')}</td>
              </tr>
              <tr className={styles.warningRow}>
                <td>Total Paid:</td>
                <td><strong>{formatCurrency(paymentSummary.totalPaid)}</strong></td>
              </tr>
              <tr>
                <td>Aging - Current:</td>
                <td>{formatCurrency(paymentSummary.statistics['0'])}</td>
              </tr>
              <tr>
                <td>1 - 30:</td>
                <td>{formatCurrency(paymentSummary.statistics['1_30'])}</td>
              </tr>
              <tr>
                <td>31 - 60:</td>
                <td>{formatCurrency(paymentSummary.statistics['31_60'])}</td>
              </tr>
              <tr>
                <td>61 - 90:</td>
                <td>{formatCurrency(paymentSummary.statistics['61_90'])}</td>
              </tr>
              <tr>
                                 <td>&gt; 90:</td>
                <td>{formatCurrency(paymentSummary.statistics['over_90'])}</td>
              </tr>
              <tr className={styles.warningRow}>
                <td>Total Outstanding:</td>
                <td><strong>{formatCurrency(paymentSummary.totalOutstanding)}</strong></td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* Main Content */}
      <div className={styles.mainContent}>
        <div className={styles.panel}>
          <div className={styles.panelHeader}>
            <h3 className={styles.panelTitle}>Expense Account</h3>
            <div className={styles.panelActions}>
              <button className={styles.btnSecondary} onClick={() => navigate('/finance/expense-account/create')}>
                <i className="glyphicon glyphicon-plus"></i> Add Expenses
              </button>
              <button className={styles.btnSecondary} onClick={() => toast.info(`Total amount due today: ${formatCurrency(paymentSummary.totalAmountDueToday)}`)}>
                Calculate Daily Total
              </button>
            </div>
          </div>

          <div className={styles.panelBody}>
            {/* Filters */}
            <form onSubmit={handleSearch} className={styles.filtersForm}>
              <div className={styles.filtersRow}>
                <div className={styles.filterGroup}>
                  <label htmlFor="category_id">Category:</label>
                  <select
                    id="category_id"
                    name="category_id"
                    value={filters.category_id}
                    onChange={(e) => handleFilterChange('category_id', e.target.value)}
                  >
                    {filterOptions.categories.map(category => {
                      // Ensure value is always a string - use empty string for "All" option
                      const categoryValue = category.id === '' || category.id === null || category.id === undefined 
                        ? '' 
                        : String(category.id);
                      const categoryName = category.name || category || 'All';
                      return (
                        <option key={categoryValue || 'all'} value={categoryValue}>{categoryName}</option>
                      );
                    })}
                  </select>
                </div>

                <div className={styles.filterGroup}>
                  <label htmlFor="type_id">Type:</label>
                  <select
                    id="type_id"
                    name="type_id"
                    value={filters.type_id}
                    onChange={(e) => handleFilterChange('type_id', e.target.value)}
                  >
                    {filterOptions.types.map(type => {
                      // Ensure value is always a string - use empty string for "All" option
                      const typeValue = type.id === '' || type.id === null || type.id === undefined 
                        ? '' 
                        : String(type.id);
                      const typeName = type.name || type || 'All';
                      return (
                        <option key={typeValue || 'all'} value={typeValue}>{typeName}</option>
                      );
                    })}
                  </select>
                </div>

                <div className={styles.filterGroup}>
                  <label htmlFor="bank_account_id">Bank Account:</label>
                  <select
                    id="bank_account_id"
                    name="bank_account_id"
                    value={filters.bank_account_id}
                    onChange={(e) => handleFilterChange('bank_account_id', e.target.value)}
                  >
                    {filterOptions.bankAccounts.map(account => {
                      // Ensure value is always a string - use empty string for "All" option
                      const accountValue = account.id === '' || account.id === null || account.id === undefined 
                        ? '' 
                        : String(account.id);
                      const accountName = account.name || account || 'All';
                      return (
                        <option key={accountValue || 'all'} value={accountValue}>{accountName}</option>
                      );
                    })}
                  </select>
                </div>

                <div className={styles.filterGroup}>
                  <label htmlFor="creditor">Creditor:</label>
                  <input
                    type="text"
                    id="creditor"
                    name="creditor"
                    value={filters.creditor}
                    onChange={(e) => handleFilterChange('creditor', e.target.value)}
                    placeholder="Enter creditor name"
                  />
                </div>

                <div className={styles.filterGroup}>
                  <label htmlFor="date_from">Date from:</label>
                  <input
                    type="date"
                    id="date_from"
                    name="date_from"
                    value={filters.date_from}
                    onChange={(e) => handleFilterChange('date_from', e.target.value)}
                  />
                </div>

                <div className={styles.filterGroup}>
                  <label htmlFor="date_to">Date to:</label>
                  <input
                    type="date"
                    id="date_to"
                    name="date_to"
                    value={filters.date_to}
                    onChange={(e) => handleFilterChange('date_to', e.target.value)}
                  />
                </div>
              </div>

              <div className={styles.searchButtonRow}>
                <button 
                  type="submit" 
                  className={styles.btnSecondary}
                >
                  Search
                </button>
                <button 
                  type="button" 
                  className={styles.btnSecondary}
                  onClick={handleClearFilters}
                >
                  Clear Filters
                </button>
              </div>
            </form>

            {/* DataTables Controls */}
            <div className={styles.datatableControls}>
              <div className={styles.entriesControl}>
                <label>Show entries:</label>
                <select 
                  className={styles.entriesSelect}
                  value={tableControls.entriesPerPage}
                  onChange={(e) => handleTableControlChange('entriesPerPage', e.target.value)}
                >
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={30}>30</option>
                  <option value={50}>50</option>
                  <option value={75}>75</option>
                  <option value={100}>100</option>
                </select>
              </div>

              <div className={styles.searchControl}>
                <label>Search:</label>
                <input
                  ref={searchInputRef}
                  type="text"
                  className={styles.searchInput}
                  value={tableControls.search}
                  onChange={handleSearchInputChange}
                  onFocus={handleSearchFocus}
                  onBlur={handleSearchBlur}
                  placeholder="Search expenses..."
                  autoComplete="off"
                />
              </div>

                             <div className={styles.actionButtons}>
                 <button 
                   className={styles.exportBtn}
                   onClick={handleExportCSV}
                   title="Export as CSV"
                 >
                   Export as CSV
                 </button>
                 <button 
                   className={styles.exportBtn}
                   onClick={handleExportPDF}
                   title="Export as PDF"
                 >
                   Export as PDF
                 </button>
                 <button 
                   className={styles.exportBtn}
                   onClick={handlePrintTable}
                   title="Print table"
                 >
                   Print table
                 </button>
                 <div className={styles.visibilityBtnContainer}>
                   <button 
                     className={styles.visibilityBtn}
                     onClick={handleColumnVisibility}
                   >
                     Column visibility
                   </button>
                   {showColumnVisibility && (
                     <div className={styles.columnVisibilityDropdown}>
                       <h4>Toggle Columns:</h4>
                       <label className={styles.columnToggle}>
                         <input
                           type="checkbox"
                           checked={columnVisibility.property}
                           onChange={() => toggleColumnVisibility('property')}
                         />
                         Property
                       </label>
                       <label className={styles.columnToggle}>
                         <input
                           type="checkbox"
                           checked={columnVisibility.category}
                           onChange={() => toggleColumnVisibility('category')}
                         />
                         Category
                       </label>
                       <label className={styles.columnToggle}>
                         <input
                           type="checkbox"
                           checked={columnVisibility.type}
                           onChange={() => toggleColumnVisibility('type')}
                         />
                         Type
                       </label>
                       <label className={styles.columnToggle}>
                         <input
                           type="checkbox"
                           checked={columnVisibility.po}
                           onChange={() => toggleColumnVisibility('po')}
                         />
                         PO
                       </label>
                       <label className={styles.columnToggle}>
                         <input
                           type="checkbox"
                           checked={columnVisibility.invoice}
                           onChange={() => toggleColumnVisibility('invoice')}
                         />
                         Invoice
                       </label>
                       <label className={styles.columnToggle}>
                         <input
                           type="checkbox"
                           checked={columnVisibility.ticketId}
                           onChange={() => toggleColumnVisibility('ticketId')}
                         />
                         Ticket ID
                       </label>
                       <label className={styles.columnToggle}>
                         <input
                           type="checkbox"
                           checked={columnVisibility.company}
                           onChange={() => toggleColumnVisibility('company')}
                         />
                         Company
                       </label>
                       <label className={styles.columnToggle}>
                         <input
                           type="checkbox"
                           checked={columnVisibility.invoiceDate}
                           onChange={() => toggleColumnVisibility('invoiceDate')}
                         />
                         Invoice Date
                       </label>
                       <label className={styles.columnToggle}>
                         <input
                           type="checkbox"
                           checked={columnVisibility.dueDate}
                           onChange={() => toggleColumnVisibility('dueDate')}
                         />
                         Due Date
                       </label>
                       <label className={styles.columnToggle}>
                         <input
                           type="checkbox"
                           checked={columnVisibility.amountDue}
                           onChange={() => toggleColumnVisibility('amountDue')}
                         />
                         Amount Due
                       </label>
                       <label className={styles.columnToggle}>
                         <input
                           type="checkbox"
                           checked={columnVisibility.totalPaid}
                           onChange={() => toggleColumnVisibility('totalPaid')}
                         />
                         Total Paid
                       </label>
                       <label className={styles.columnToggle}>
                         <input
                           type="checkbox"
                           checked={columnVisibility.age}
                           onChange={() => toggleColumnVisibility('age')}
                         />
                         Age
                       </label>
                       <label className={styles.columnToggle}>
                         <input
                           type="checkbox"
                           checked={columnVisibility.outstanding}
                           onChange={() => toggleColumnVisibility('outstanding')}
                         />
                         Outstanding
                       </label>
                       <label className={styles.columnToggle}>
                         <input
                           type="checkbox"
                           checked={columnVisibility.xeroPayment}
                           onChange={() => toggleColumnVisibility('xeroPayment')}
                         />
                         In Xero
                       </label>
                       <label className={styles.columnToggle}>
                         <input
                           type="checkbox"
                           checked={columnVisibility.actions}
                           onChange={() => toggleColumnVisibility('actions')}
                         />
                         Actions
                       </label>
                     </div>
                   )}
                 </div>
               </div>
            </div>

            <div className={styles.resultCount}>
              (Showing {paginatedExpenses.length} of {totalRecords} results)
            </div>

            {/* DataTable */}
            <div className={styles.expensesTableContainer} style={{ position: 'relative' }}>
              {tableLoading && (
                <div style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backgroundColor: 'rgba(255, 255, 255, 0.7)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  zIndex: 10
                }}>
                  <span>Loading...</span>
                </div>
              )}
              <table className={styles.expensesTable}>
                <thead>
                  <tr>
                    {columnVisibility.property && (
                      <th
                        style={{ cursor: 'pointer', userSelect: 'none' }}
                        onClick={() => handleSort('property')}
                        title="Click to sort by Property"
                      >
                        Property{getSortIcon('property')}
                      </th>
                    )}
                    {columnVisibility.category && (
                      <th
                        style={{ cursor: 'pointer', userSelect: 'none' }}
                        onClick={() => handleSort('category')}
                        title="Click to sort by Category"
                      >
                        Category{getSortIcon('category')}
                      </th>
                    )}
                    {columnVisibility.type && (
                      <th
                        style={{ cursor: 'pointer', userSelect: 'none' }}
                        onClick={() => handleSort('type')}
                        title="Click to sort by Type"
                      >
                        Type{getSortIcon('type')}
                      </th>
                    )}
                    {columnVisibility.po && (
                      <th
                        style={{ cursor: 'pointer', userSelect: 'none' }}
                        onClick={() => handleSort('po')}
                        title="Click to sort by PO"
                      >
                        PO{getSortIcon('po')}
                      </th>
                    )}
                    {columnVisibility.invoice && (
                      <th
                        style={{ cursor: 'pointer', userSelect: 'none' }}
                        onClick={() => handleSort('invoice')}
                        title="Click to sort by Invoice"
                      >
                        Invoice{getSortIcon('invoice')}
                      </th>
                    )}
                    {columnVisibility.ticketId && (
                      <th
                        style={{ cursor: 'pointer', userSelect: 'none' }}
                        onClick={() => handleSort('ticketId')}
                        title="Click to sort by Ticket ID"
                      >
                        Ticket Id{getSortIcon('ticketId')}
                      </th>
                    )}
                    {columnVisibility.company && (
                      <th
                        style={{ cursor: 'pointer', userSelect: 'none' }}
                        onClick={() => handleSort('company')}
                        title="Click to sort by Company"
                      >
                        Company{getSortIcon('company')}
                      </th>
                    )}
                    {columnVisibility.invoiceDate && (
                      <th
                        style={{ cursor: 'pointer', userSelect: 'none' }}
                        onClick={() => handleSort('invoiceDate')}
                        title="Click to sort by Invoice Date"
                      >
                        Invoice Date{getSortIcon('invoiceDate')}
                      </th>
                    )}
                    {columnVisibility.dueDate && (
                      <th
                        style={{ cursor: 'pointer', userSelect: 'none' }}
                        onClick={() => handleSort('dueDate')}
                        title="Click to sort by Due Date"
                      >
                        Due Date{getSortIcon('dueDate')}
                      </th>
                    )}
                    {columnVisibility.amountDue && (
                      <th
                        style={{ cursor: 'pointer', userSelect: 'none' }}
                        onClick={() => handleSort('amountDue')}
                        title="Click to sort by Amount Due"
                      >
                        Amount Due{getSortIcon('amountDue')}
                      </th>
                    )}
                    {columnVisibility.totalPaid && (
                      <th
                        style={{ cursor: 'pointer', userSelect: 'none' }}
                        onClick={() => handleSort('totalPaid')}
                        title="Click to sort by Total Paid"
                      >
                        Total Paid{getSortIcon('totalPaid')}
                      </th>
                    )}
                    {columnVisibility.age && (
                      <th
                        style={{ cursor: 'pointer', userSelect: 'none' }}
                        onClick={() => handleSort('age')}
                        title="Click to sort by Age"
                      >
                        Age{getSortIcon('age')}
                      </th>
                    )}
                    {columnVisibility.outstanding && (
                      <th
                        style={{ cursor: 'pointer', userSelect: 'none' }}
                        onClick={() => handleSort('outstanding')}
                        title="Click to sort by Outstanding"
                      >
                        Outstanding{getSortIcon('outstanding')}
                      </th>
                    )}
                    {columnVisibility.xeroPayment && (
                      <th title="What Xero shows for this invoice. Read-only.">In Xero</th>
                    )}
                    {columnVisibility.actions && <th className={styles.actionHead}>Actions</th>}
                  </tr>
                </thead>
                <tbody>
                  {paginatedExpenses.length > 0 ? (
                    paginatedExpenses.map((expense, index) => (
                      <tr key={expense.id} className={index % 2 === 0 ? styles.evenRow : styles.oddRow}>
                        {columnVisibility.property && (
                          <td>
                            {expense.propertyId ? (
                              <a href={`/properties/${expense.propertyId}`} className={styles.propertyLink}>
                                {expense.propertyName}
                              </a>
                            ) : (
                              expense.propertyName
                            )}
                          </td>
                        )}
                        {columnVisibility.category && <td>{expense.categoryName}</td>}
                        {columnVisibility.type && <td>{expense.typeName}</td>}
                        {columnVisibility.po && <td>{expense.purchase_order_number}</td>}
                        {columnVisibility.invoice && <td>{expense.invoice_number}</td>}
                        {columnVisibility.ticketId && (
                          <td>
                            {expense.ticket_id ? (
                              <a href={`/tickets/${expense.ticket_id}`} className={styles.ticketLink}>
                                {expense.ticket_id}
                              </a>
                            ) : (
                              '-'
                            )}
                          </td>
                        )}
                        {columnVisibility.company && <td>{expense.creditor}</td>}
                        {columnVisibility.invoiceDate && <td>{formatDate(expense.date)}</td>}
                        {columnVisibility.dueDate && <td>{formatDate(expense.due_date)}</td>}
                        {columnVisibility.amountDue && <td className={styles.amountCell}>{formatCurrency(expense.amount)}</td>}
                        {columnVisibility.totalPaid && <td className={styles.amountCell}>{formatCurrency(expense.amount_paid)}</td>}
                        {columnVisibility.age && <td>{calculateAge(expense.due_date)}</td>}
                        {columnVisibility.outstanding && <td className={styles.amountCell}>{formatCurrency(expense.amount_outstanding || (expense.amount - expense.amount_paid))}</td>}
                        {/* What Xero says. This list is the outstanding items, so "Xero shows
                            this paid" is the case worth catching — it means the payment has
                            happened but MyGlobe has not been told. */}
                        {columnVisibility.xeroPayment && (
                          <td>
                            {(() => {
                              const x = xeroPayments[expense.id];
                              if (!x) return <span style={{ color: '#adb5bd' }}>—</span>;

                              const label =
                                x.payment_state === 'paid'
                                  ? 'Paid'
                                  : x.payment_state === 'part_paid'
                                  ? 'Part paid'
                                  : 'Outstanding';
                              const colour =
                                x.payment_state === 'paid'
                                  ? '#198754'
                                  : x.payment_state === 'part_paid'
                                  ? '#fd7e14'
                                  : '#6c757d';

                              const title = [
                                `Xero: ${label}`,
                                x.amount_paid ? `£${Number(x.amount_paid).toFixed(2)}` : null,
                                x.fully_paid_on
                                  ? `on ${new Date(x.fully_paid_on).toLocaleDateString('en-GB')}`
                                  : null,
                                // The account Xero paid from — the one thing MyGlobe cannot
                                // work out for itself.
                                x.payment_account_name ? `from ${x.payment_account_name}` : null,
                              ]
                                .filter(Boolean)
                                .join(' ');

                              return (
                                <a
                                  href={x.xero_url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  title={title}
                                  style={{ color: colour, textDecoration: 'none', fontWeight: 600 }}
                                >
                                  {label}
                                </a>
                              );
                            })()}
                          </td>
                        )}
                        {columnVisibility.actions && (
                          <td className={styles.actionsColumn}>
                            <div className={styles.actionButtonsGroup}>
                              {expense.file && (
                                <button 
                                  className={`${styles.actionBtn} ${styles.btnWarning}`}
                                  onClick={() => handleExpenseAction('download', expense.id)}
                                  title="Download"
                                >
                                  Download
                                </button>
                              )}
                              <button 
                                className={`${styles.actionBtn} ${styles.btnPrimary}`}
                                onClick={() => handleExpenseAction('edit', expense.id)}
                                title="Edit"
                              >
                                Edit
                              </button>
                              {expense.status === 0 && (
                                <button 
                                  className={`${styles.actionBtn} ${styles.btnSuccess}`}
                                  onClick={() => handleExpenseAction('pay', expense.id)}
                                  title="Pay"
                                >
                                  Pay
                                </button>
                              )}
                              <button 
                                className={`${styles.actionBtn} ${styles.btnDanger}`}
                                onClick={() => handleExpenseAction('delete', expense.id)}
                                title="Remove"
                              >
                                Remove
                              </button>
                            </div>
                          </td>
                        )}
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={Object.values(columnVisibility).filter(Boolean).length} className={styles.noDataCell}>
                        No expenses found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalRecords > 0 && (
              <div className={styles.pagination}>
                <div className={styles.paginationInfo}>
                  Showing {((currentPage - 1) * parseInt(tableControls.entriesPerPage, 10) || 30) + 1} to {Math.min(currentPage * parseInt(tableControls.entriesPerPage, 10) || 30, totalRecords)} of {totalRecords} entries
                </div>
                <div className={styles.paginationControls}>
                  <button 
                    className={styles.paginationBtn}
                    onClick={goToPreviousPage}
                    disabled={currentPage === 1}
                  >
                    Previous
                  </button>
                  
                  {getPageNumbers().map((page, index) => {
                    if (page === 'ellipsis-start' || page === 'ellipsis-end') {
                      return (
                        <span key={`ellipsis-${index}`} className={styles.paginationEllipsis}>
                          ...
                        </span>
                      );
                    }
                    return (
                      <button
                        key={page}
                        className={`${styles.paginationBtn} ${page === currentPage ? styles.activePage : ''}`}
                        onClick={() => goToPage(page)}
                      >
                        {page}
                      </button>
                    );
                  })}
                  
                  <button 
                    className={styles.paginationBtn}
                    onClick={goToNextPage}
                    disabled={currentPage === totalPages}
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExpenseAccount; 